import React, { useState, useMemo, useCallback } from 'react'
import { Spin, Select as AntSelect, Button, Input, Upload, message } from 'antd'
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import { Field } from 'react-final-form'
import FieldWrapper from 'components/FieldWrapper/FieldWrapper.js'
import { getSellerPartners, searchSellerPartners, createSellerPartnerApi } from 'domains/shops/api'
import { getBase64, checkFileBeforeUpload } from 'helpers/files'
import { StyledSelect, CreateSection, LogoUploadWrapper } from './SearchSellerPartner.styles'
import debounce from 'lodash/debounce'

const SearchSellerPartner = ({ name, validate, disabled, initialValue }) => {
  const [data, setData] = useState([])
  const [fetching, setFetching] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [newPartnerName, setNewPartnerName] = useState('')
  const [newPartnerStoreUrl, setNewPartnerStoreUrl] = useState('')
  const [newPartnerLogo, setNewPartnerLogo] = useState(null)
  const [logoFileList, setLogoFileList] = useState([])
  const [logoLoading, setLogoLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  const fetchSellerPartners = useCallback(async () => {
    if (data.length > 0) return
    setFetching(true)
    try {
      const response = await getSellerPartners()
      if (response?.data?.data) {
        setData(response.data.data)
      }
    } catch (error) {
      console.error(error)
      message.error('Failed to fetch seller partners')
    } finally {
      setFetching(false)
    }
  }, [data.length])

  const handleSearch = useMemo(
    () =>
      debounce(async (value) => {
        if (!value || value.length < 2) {
          fetchSellerPartners()
          return
        }
        setFetching(true)
        try {
          const response = await searchSellerPartners(value)
          if (response?.data?.data) {
            setData(response.data.data)
          }
        } catch (error) {
          console.error(error)
          message.error('Failed to search seller partners')
        } finally {
          setFetching(false)
        }
      }, 400),
    [fetchSellerPartners]
  )

  const handleLogoChange = useCallback(({ file, fileList }) => {
    if (file.status === 'uploading') {
      setLogoLoading(true)
      setLogoFileList([...fileList])
    }
    if (file.status === 'done') {
      getBase64(file.originFileObj, (imageUrl) => {
        setNewPartnerLogo(imageUrl)
        setLogoLoading(false)
        setLogoFileList([{ url: imageUrl, uid: file.uid }])
      })
    }
  }, [])

  const handleLogoRemove = useCallback(() => {
    setNewPartnerLogo(null)
    setLogoFileList([])
  }, [])

  const resetCreateForm = useCallback(() => {
    setShowCreate(false)
    setNewPartnerName('')
    setNewPartnerStoreUrl('')
    setNewPartnerLogo(null)
    setLogoFileList([])
  }, [])

  const handleCreate = useCallback(
    async (onChange) => {
      if (!newPartnerName.trim()) return
      setCreating(true)
      try {
        const requestData = {
          type: 'seller_partners',
          attributes: {
            name: newPartnerName.trim(),
            'store_url': newPartnerStoreUrl.trim() || null,
            logo_url_base64: newPartnerLogo,
            active: true,
          },
        }
        const response = await createSellerPartnerApi(requestData)
        if (response?.data?.data) {
          const newPartner = response.data.data
          setData((prev) => [newPartner, ...prev])
          onChange(parseInt(newPartner.id))
          resetCreateForm()
          message.success('Seller partner created')
        }
      } catch (error) {
        console.error(error)
        message.error('Failed to create seller partner')
      } finally {
        setCreating(false)
      }
    },
    [newPartnerName, newPartnerStoreUrl, newPartnerLogo, resetCreateForm]
  )

  return (
    <Field name={name} validate={validate} initialValue={initialValue}>
      {({ meta, input }) => (
        <FieldWrapper label="Seller Partner" meta={meta}>
          <StyledSelect
            disabled={disabled}
            invalid={meta.touched && meta.error}
            value={input?.value || undefined}
            showSearch
            placeholder="Search seller partners..."
            notFoundContent={fetching ? <Spin size="small" /> : null}
            filterOption={false}
            onDropdownVisibleChange={(open) => open && fetchSellerPartners()}
            onSearch={handleSearch}
            onChange={(value) => {
              input.onChange(value)
            }}
          >
            {data?.map((item) => (
              <AntSelect.Option
                key={item.id}
                value={parseInt(item.id)}
              >
                {item.id} - {item.attributes?.name}
              </AntSelect.Option>
            ))}
          </StyledSelect>
          {!disabled && (
            <>
              <Button
                type="link"
                size="small"
                onClick={() => showCreate ? resetCreateForm() : setShowCreate(true)}
                style={{ padding: 0, marginTop: 4 }}
              >
                {showCreate ? 'Cancel' : '+ Create New Seller Partner'}
              </Button>
              {showCreate && (
                <CreateSection>
                  <Input
                    placeholder="Partner name (required)"
                    value={newPartnerName}
                    onChange={(e) => setNewPartnerName(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  <Input
                    placeholder="Store URL (optional)"
                    value={newPartnerStoreUrl}
                    onChange={(e) => setNewPartnerStoreUrl(e.target.value)}
                    style={{ marginBottom: 8 }}
                  />
                  <LogoUploadWrapper>
                    <span style={{ display: 'block', marginBottom: 4, color: '#888' }}>
                      Logo (required)
                    </span>
                    <Upload
                      accept="image/jpeg,image/png"
                      listType="picture-card"
                      maxCount={1}
                      fileList={logoFileList}
                      customRequest={({ onSuccess }) => {
                        Promise.resolve().then(() => onSuccess())
                      }}
                      beforeUpload={checkFileBeforeUpload()}
                      onChange={handleLogoChange}
                      onRemove={handleLogoRemove}
                      showUploadList={{
                        showRemoveIcon: true,
                        showPreviewIcon: false,
                      }}
                    >
                      {!newPartnerLogo && (
                        <div>
                          {logoLoading ? <LoadingOutlined /> : <PlusOutlined />}
                          <div style={{ marginTop: 4, fontSize: 12 }}>Upload</div>
                        </div>
                      )}
                    </Upload>
                  </LogoUploadWrapper>
                  <Button
                    type="primary"
                    size="small"
                    loading={creating}
                    disabled={!newPartnerName.trim() || !newPartnerLogo}
                    onClick={() => handleCreate(input.onChange)}
                  >
                    Create
                  </Button>
                </CreateSection>
              )}
            </>
          )}
        </FieldWrapper>
      )}
    </Field>
  )
}

export default SearchSellerPartner
